import { useEffect, useRef, useState } from 'react';
import moment from 'moment';
import { singletonHook } from 'react-singleton-hook';
import useEmployeeSuggestionsService from '../employeeSuggestionsService';
import { DATE_FORMAT } from 'framework/utils';
import { ErrorMessage, useMessageService } from 'framework/ui/Message';

const initState = { hookLoading: true };

const WAITING_TIMEOUT = 100;
const ERROR_MESSAGE_COOLDOWN_MS = 60_000;
const EMPLOYEE_NAMES_ERROR_TEXT = 'Failed to load employee names. Please try again later.';

/**
 * Singleton hook that collects employee names.
 *
 * It doesn't send a request immediately and wait some
 * time and collect employee aliases from multiple
 * components.
 */
const useEmployeeNamesInfoReceiverImpl = () => {
  const { searchWithContractors } = useEmployeeSuggestionsService();
  const { addMessage } = useMessageService();
  const lastErrorMessageAtRef = useRef(0);

  const [promisesTimeout, setPromisesTimeout] = useState<number | boolean>(false);
  const [promises, setPromises] = useState<any[]>([]);
  const [employeeNames, setEmployeeNames] = useState<any[]>([]);

  useEffect(() => {
    if (promisesTimeout) clearTimeout(promisesTimeout as number);

    if (promises.length && employeeNames.length) {
      const newTimeout = setTimeout(async () => {
        const updatedAt = moment().format(DATE_FORMAT.API);
        // @ts-ignore
        const uniqueEmployeeNames = [...new Set(employeeNames)];
        const shouldHandleError = promises.some(p => p?.isHandleError);

        try {
          const info = await searchWithContractors({
            pageSize: uniqueEmployeeNames.length,
            filter: {
              alias: { in: uniqueEmployeeNames },
            },
            pageNumber: 0,
          });

          if ((info as any)?.error && shouldHandleError) {
            const now = Date.now();
            if (now - lastErrorMessageAtRef.current >= ERROR_MESSAGE_COOLDOWN_MS) {
              lastErrorMessageAtRef.current = now;
              addMessage(new ErrorMessage(EMPLOYEE_NAMES_ERROR_TEXT));
            }
          }

          const results = Array.isArray((info as any)?.result) ? (info as any).result : [];
          const resolved = results.reduce(
            (acc: Dictionary<any>, result: any) => ({ ...acc, [result.alias]: { ...result, updatedAt } }),
            {},
          );

          promises.forEach(p => p.resolve(resolved));
        } catch (error) {
          if (shouldHandleError) {
            const now = Date.now();
            if (now - lastErrorMessageAtRef.current >= ERROR_MESSAGE_COOLDOWN_MS) {
              lastErrorMessageAtRef.current = now;
              addMessage(new ErrorMessage(EMPLOYEE_NAMES_ERROR_TEXT));
            }
          }
          promises.forEach(p => p.resolve({}));
        } finally {
          setPromisesTimeout(false);
          setEmployeeNames([]);
          setPromises([]);
        }
      }, WAITING_TIMEOUT);
      setPromisesTimeout(newTimeout as any);
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [promises, employeeNames]);

  const collectEmployeeNamesData = async (initialEmployeeAliases: any[], isHandleError = false) => {
    const employeeAliases = initialEmployeeAliases.filter((id: any) => id && id !== 'undefined');

    if (!employeeAliases || !employeeAliases.length) {
      return Promise.resolve({});
    }

    const promise = new Promise((resolve, reject) =>
      setPromises((old: any) => [...old, { resolve, reject, isHandleError }]),
    );

    setEmployeeNames((old: any) => [...old, ...employeeAliases]);

    return promise;
  };

  return {
    collectEmployeeNamesData,
  };
};

const useEmployeeNamesInfoReceiver = singletonHook(initState as any, useEmployeeNamesInfoReceiverImpl as any);

export default useEmployeeNamesInfoReceiver;
